import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2, Flag, CircleDot, GitFork } from 'lucide-react';
import clsx from 'clsx';
import {
  CATEGORY_LABELS,
  STATE_CATEGORIES,
  blankDecision,
  blankState,
  slugify,
  uniqueId,
  type Workflow,
  type WorkflowState,
} from './workflowModel';

interface Props {
  workflow: Workflow;
  /** How many entities sit in each state id, from /workflows/:id/usage. */
  stateCounts: Record<string, number>;
  onChange: (states: WorkflowState[]) => void;
  unreachable: string[];
  readOnly?: boolean;
}

const INPUT =
  'px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60';

/**
 * Column template shared by the header and every row.
 *
 * A CSS grid rather than a table: `@hello-pangea/dnd` lifts the dragged element
 * out of its parent, and a `<tr>` torn out of a `<table>` loses every column
 * width, so the drag preview collapses. A grid row drags intact.
 */
const GRID =
  'grid grid-cols-[2rem_9rem_7.5rem_7.5rem_2.5rem_4rem_4rem_3rem_3rem_9rem_4rem_2rem] gap-1 items-center min-w-[64rem]';

export default function StatesEditor({
  workflow, stateCounts, onChange, unreachable, readOnly = false,
}: Props) {
  const states = workflow.states;

  const patch = (index: number, updates: Partial<WorkflowState>) =>
    onChange(states.map((s, i) => (i === index ? { ...s, ...updates } : s)));

  const reorder = (from: number, to: number) => {
    if (to < 0 || to >= states.length || from === to) return;
    const next = [...states];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next.map((s, i) => ({ ...s, order: i })));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    reorder(result.source.index, result.destination.index);
  };

  const add = (kind: 'state' | 'decision' = 'state') => {
    const make = kind === 'decision' ? blankDecision : blankState;
    onChange([...states, make(states.length, states.map((s) => s.id))]);
  };

  const remove = (index: number) => {
    const state = states[index];
    const held = stateCounts[state.id] || 0;
    const referenced = workflow.transitions.filter(
      (t) => t.to === state.id || t.from.includes(state.id)
    ).length;
    const warnings = [
      held ? `${held} item${held === 1 ? '' : 's'} currently sit in it` : '',
      referenced ? `${referenced} transition${referenced === 1 ? '' : 's'} reference it` : '',
    ].filter(Boolean);
    const detail = warnings.length ? `\n\n${warnings.join(', and ')}.` : '';
    if (!confirm(`Remove the state "${state.label}"?${detail}`)) return;
    onChange(states.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })));
  };

  /** Exactly one initial state, so setting one clears the others. */
  const setInitial = (index: number) =>
    onChange(states.map((s, i) => ({ ...s, isInitial: i === index })));

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          States
          <span className="ml-2 font-normal text-xs text-gray-500 dark:text-gray-400">
            board columns — drag to reorder
          </span>
        </h3>
        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => add('state')}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded text-accent hover:bg-accent/10"
            >
              <Plus size={13} /> Add state
            </button>
            <button
              onClick={() => add('decision')}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded text-amber-600 hover:bg-amber-500/10"
              title="A node items are routed through, not parked on"
            >
              <GitFork size={13} /> Add decision
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        {/* Header */}
        <div className={`${GRID} text-[11px] text-gray-500 dark:text-gray-400 pb-1`}>
          <span />
          <span>Label</span>
          <span>Id</span>
          <span>Category</span>
          <span title="Colour">Col</span>
          <span title="0 = unlimited">WIP</span>
          <span title="0 = no SLA">SLA h</span>
          <span className="text-center" title="Exactly one start state">Start</span>
          <span className="text-center" title="At least one end state">End</span>
          <span>Required to enter</span>
          <span className="text-right">Items</span>
          <span />
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="states" isDropDisabled={readOnly}>
            {(dropProvided, dropSnapshot) => (
              <div
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                className={clsx(
                  'rounded transition-colors',
                  dropSnapshot.isDraggingOver && 'bg-accent/5'
                )}
              >
                {states.map((state, index) => {
                  const held = stateCounts[state.id] || 0;
                  const isUnreachable = unreachable.includes(state.id);
                  // Nothing rests on a decision, so category, WIP, SLA,
                  // start/end and entry requirements are meaningless for one.
                  const isDecision = state.kind === 'decision';
                  const restOnly = readOnly || isDecision;

                  return (
                    <Draggable
                      key={`${state.id}-${index}`}
                      draggableId={`${state.id}-${index}`}
                      index={index}
                      isDragDisabled={readOnly}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={clsx(
                            GRID,
                            'border-t border-gray-100 dark:border-gray-700 py-1 bg-white dark:bg-gray-800',
                            dragSnapshot.isDragging &&
                              'shadow-lg rounded ring-1 ring-accent/40 border-t-0'
                          )}
                          style={dragProvided.draggableProps.style}
                        >
                          {/* Drag handle */}
                          <div
                            {...dragProvided.dragHandleProps}
                            className={clsx(
                              'flex items-center justify-center rounded',
                              readOnly
                                ? 'text-gray-200 dark:text-gray-700'
                                : 'text-gray-300 dark:text-gray-600 hover:text-accent cursor-grab active:cursor-grabbing'
                            )}
                            title={readOnly ? undefined : `Drag to reorder ${state.label}`}
                            aria-label={readOnly ? undefined : `Drag to reorder ${state.label}`}
                          >
                            <GripVertical size={14} />
                          </div>

                          <div className="flex items-center gap-1 min-w-0">
                          {isDecision && (
                            <span title="Decision — routed through, not rested on" className="shrink-0">
                              <GitFork size={12} className="text-amber-500" />
                            </span>
                          )}
                          <input
                            value={state.label}
                            disabled={readOnly}
                            aria-label={`Label for state ${state.id}`}
                            onChange={(e) => {
                              const label = e.target.value;
                              // Keep the id in step only while it still looks
                              // generated and nothing relies on it yet.
                              const derived = slugify(state.label);
                              const keepInStep =
                                state.id === derived || state.id.startsWith('state');
                              patch(index, {
                                label,
                                ...(keepInStep && !held
                                  ? {
                                      id: uniqueId(
                                        slugify(label),
                                        states.filter((_, i) => i !== index).map((s) => s.id)
                                      ),
                                    }
                                  : {}),
                              });
                            }}
                            className={`${INPUT} w-full`}
                          />
                          </div>

                          <input
                            value={state.id}
                            disabled={readOnly}
                            aria-label={`Id for state ${state.label}`}
                            onChange={(e) => patch(index, { id: slugify(e.target.value) })}
                            className={`${INPUT} w-full font-mono text-[11px]`}
                            title={
                              held
                                ? 'Changing this id strands the items currently in this state'
                                : 'Stable identifier stored on each item'
                            }
                          />

                          <select
                            value={isDecision ? '' : state.category}
                            disabled={restOnly}
                            aria-label={`Category for state ${state.label}`}
                            onChange={(e) => patch(index, { category: e.target.value })}
                            className={`${INPUT} w-full`}
                          >
                            {isDecision && <option value="">decision</option>}
                            {STATE_CATEGORIES.map((c) => (
                              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                            ))}
                          </select>

                          <input
                            type="color"
                            value={state.color}
                            disabled={readOnly}
                            aria-label={`Colour for state ${state.label}`}
                            onChange={(e) => patch(index, { color: e.target.value })}
                            className="w-8 h-6 rounded border border-gray-300 dark:border-gray-600 bg-transparent"
                          />

                          <input
                            type="number"
                            min={0}
                            value={state.wipLimit}
                            disabled={restOnly}
                            aria-label={`WIP limit for state ${state.label}`}
                            onChange={(e) => patch(index, { wipLimit: Number(e.target.value) || 0 })}
                            className={`${INPUT} w-full`}
                          />

                          <input
                            type="number"
                            min={0}
                            value={state.slaHours}
                            disabled={restOnly}
                            aria-label={`SLA hours for state ${state.label}`}
                            onChange={(e) => patch(index, { slaHours: Number(e.target.value) || 0 })}
                            className={`${INPUT} w-full`}
                          />

                          <div className="flex justify-center">
                            <button
                              onClick={() => setInitial(index)}
                              disabled={restOnly}
                              aria-pressed={state.isInitial}
                              aria-label={`Mark ${state.label} as the start state`}
                              title="Mark as the start state"
                              className={clsx(
                                'p-0.5 rounded disabled:opacity-50',
                                state.isInitial
                                  ? 'text-accent'
                                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'
                              )}
                            >
                              <CircleDot size={14} />
                            </button>
                          </div>

                          <div className="flex justify-center">
                            <button
                              onClick={() => patch(index, { isTerminal: !state.isTerminal })}
                              disabled={restOnly}
                              aria-pressed={state.isTerminal}
                              aria-label={`Mark ${state.label} as an end state`}
                              title="Mark as an end state"
                              className={clsx(
                                'p-0.5 rounded disabled:opacity-50',
                                state.isTerminal
                                  ? 'text-green-600'
                                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'
                              )}
                            >
                              <Flag size={14} />
                            </button>
                          </div>

                          <input
                            value={state.requiredFields.join(', ')}
                            disabled={restOnly}
                            aria-label={`Fields required to enter ${state.label}`}
                            onChange={(e) =>
                              patch(index, {
                                requiredFields: e.target.value
                                  .split(',')
                                  .map((f) => f.trim())
                                  .filter(Boolean),
                              })
                            }
                            placeholder="e.g. assignee"
                            className={`${INPUT} w-full`}
                            title="Comma-separated fields that must be filled to enter this state"
                          />

                          <div className="flex items-center justify-end gap-1">
                            <span
                              className={clsx(
                                'tabular-nums text-xs',
                                held
                                  ? 'text-gray-700 dark:text-gray-300'
                                  : 'text-gray-300 dark:text-gray-600'
                              )}
                              title={held ? `${held} entities are in this state` : 'Empty'}
                            >
                              {held}
                            </span>
                            {isUnreachable && (
                              <span
                                className="text-amber-500"
                                title="No transition reaches this state"
                              >
                                !
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-end">
                            {/* Keyboard path: reordering must not require a pointer. */}
                            {!readOnly && (
                              <div className="flex flex-col -space-y-1">
                                <button
                                  onClick={() => reorder(index, index - 1)}
                                  disabled={index === 0}
                                  className="p-0 text-gray-300 dark:text-gray-600 hover:text-accent disabled:opacity-30"
                                  aria-label={`Move ${state.label} up`}
                                  title="Move up"
                                >
                                  <ArrowUp size={11} />
                                </button>
                                <button
                                  onClick={() => reorder(index, index + 1)}
                                  disabled={index === states.length - 1}
                                  className="p-0 text-gray-300 dark:text-gray-600 hover:text-accent disabled:opacity-30"
                                  aria-label={`Move ${state.label} down`}
                                  title="Move down"
                                >
                                  <ArrowDown size={11} />
                                </button>
                              </div>
                            )}
                            {!readOnly && (
                              <button
                                onClick={() => remove(index)}
                                className="p-0.5 text-gray-400 hover:text-red-500"
                                aria-label={`Remove state ${state.label}`}
                                title="Remove state"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {unreachable.length > 0 && (
        <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-500">
          No transition reaches {unreachable.join(', ')} — it will show as a column
          nothing can move into. Allowed, but usually a mistake.
        </p>
      )}
    </section>
  );
}
